/**
 * NX-5175: rate x units worked out exactly, so a preview shows the figure the
 * backend saves. The backend prices a timesheet line in Big.js
 * (processTimesheetRate): rate x units exactly, rounded half up to the penny.
 * A float product is not exact: 37.5 * 13.37 is 501.37499999999994, which
 * toFixed(2) shows as 501.37 where the saved line is 501.375, i.e. 501.38.
 *
 * A value is held as an integer and its number of decimals: 13.37 -> 1337, 2.
 */
type ExactDecimal = { digits: bigint; decimals: number };

const TEN = BigInt(10);
const ZERO: ExactDecimal = { digits: BigInt(0), decimals: 0 };
const ONE_HUNDRED: ExactDecimal = { digits: BigInt(100), decimals: 0 };

/** The number parseFloat reads, held exactly; null when it is not finite. */
const toExact = (value: unknown): ExactDecimal | null => {
  const number = Number.parseFloat(String(value));
  if (!Number.isFinite(number)) return null;
  // String() gives the shortest decimal that reads back as the same number
  // ("13.37"), with an exponent for a very large or very small one.
  const [mantissa, exponent = '0'] = String(number).split('e');
  const [whole, fraction = ''] = mantissa.split('.');
  const digits = BigInt(whole + fraction);
  const decimals = fraction.length - Number(exponent);
  return decimals >= 0
    ? { digits, decimals }
    : { digits: digits * TEN ** BigInt(-decimals), decimals: 0 };
};

const times = (a: ExactDecimal, b: ExactDecimal): ExactDecimal => ({
  digits: a.digits * b.digits,
  decimals: a.decimals + b.decimals,
});

const plus = (a: ExactDecimal, b: ExactDecimal): ExactDecimal => {
  const decimals = Math.max(a.decimals, b.decimals);
  return {
    digits:
      a.digits * TEN ** BigInt(decimals - a.decimals) +
      b.digits * TEN ** BigInt(decimals - b.decimals),
    decimals,
  };
};

/** To the penny, ties away from zero (Big.roundHalfUp), as toFixed(2) text. */
const toPennies = ({ digits, decimals }: ExactDecimal): string => {
  const negative = digits < 0;
  let pennies = negative ? -digits : digits;
  if (decimals > 2) {
    const divisor = TEN ** BigInt(decimals - 2);
    const remainder = pennies % divisor;
    pennies = pennies / divisor;
    if (remainder * BigInt(2) >= divisor) pennies += BigInt(1);
  } else {
    pennies = pennies * TEN ** BigInt(2 - decimals);
  }
  const text = pennies.toString().padStart(3, '0');
  const sign = negative && pennies > 0 ? '-' : '';
  return `${sign}${text.slice(0, -2)}.${text.slice(-2)}`;
};

/**
 * A rate line's Net and Total (Net plus VAT at `vatRate` percent, "20%" or
 * 20), each rounded on its own from the exact net, as the backend saves them.
 * A value that is not a number prices the line at zero, as the backend does.
 */
export const priceRateLine = (
  rate: unknown,
  units: unknown,
  vatRate: unknown,
): { net: string; total: string } => {
  const rateValue = toExact(rate);
  const unitsValue = toExact(units);
  const vatPercent = toExact(vatRate);
  if (!rateValue || !unitsValue || !vatPercent) {
    return { net: '0.00', total: '0.00' };
  }
  const net = times(rateValue, unitsValue);
  // 1 + vat / 100, written as (100 + vat) / 100.
  const withVat = plus(ONE_HUNDRED, vatPercent);
  return {
    net: toPennies(net),
    total: toPennies(
      times(net, { digits: withVat.digits, decimals: withVat.decimals + 2 }),
    ),
  };
};

/**
 * a x b, exact and unrounded, as a number: 13.37 x 37.5 gives 501.375 where
 * floats give 501.37499999999994. Its shortest form is the exact product, and
 * numeral rounds through that form, so formatMoneyExport shows 501.38, as the
 * backend rounds it. A value that is not a number gives 0.
 */
export const timesExact = (a: unknown, b: unknown): number => {
  const aValue = toExact(a);
  const bValue = toExact(b);
  if (!aValue || !bValue) return 0;
  const { digits, decimals } = times(aValue, bValue);
  return Number(`${digits}e-${decimals}`);
};

/**
 * The sum of rate x units over the lines, rounded to the penny once, as the
 * backend totals a timesheet's net. A value that is not a number counts as 0.
 */
export const sumRateLines = (
  lines: { rate?: unknown; units?: unknown }[],
): string =>
  toPennies(
    lines.reduce(
      (sum, line) =>
        plus(
          sum,
          times(
            toExact(Number(line?.rate || 0)) ?? ZERO,
            toExact(Number(line?.units || 0)) ?? ZERO,
          ),
        ),
      ZERO,
    ),
  );
